import React from 'react';
// @ts-ignore
import * as d3 from 'd3';
import styled from 'styled-components';
// @ts-ignore
import classNames from 'classnames';

import BorderBox from './BorderBox';
import HorizontalAxis from './HorizontalAxis';
import VerticalAxis from './VerticalAxis';

export const MARGIN = {
  top: 20,
  bottom: 40,
  left: 100,
  right: 20
};

const SERIES_COLORS: any = {
  'G7': {
    'stroke': '#1E6BD1',
    'fill': '#779DCF'
  },
  'BRICS': {
    'stroke': '#4E8F7E',
    'fill': '#B99CC4'
  },
  'RDM': {
    'stroke': '#A44FD1',
    'fill': '#D5987D'
  },
  'REM': {
    'stroke': '#DA7F52',
    'fill': '#8EC1B6'
  }
};

interface BpsRecord {
  region: string,
  year: number,
  bps: number
}

interface BpsSeriesProps {
  bpsRecords: BpsRecord[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  stroke: string,
  fill: string,
  chartHeight: number,
  isDisabled: boolean,
};

const StyledBpsSeries = styled.path`
  stroke-width: 5px;
  stroke-linecap: round;
  stroke-linejoin: miter;
  box-shadow: 0px 6px 2px #00000029;

  &.is-disabled {
    stroke-opacity: 0.25;
    fill-opacity: 0.25;
  }
`;

const BpsSeries = ({ bpsRecords, xScale, yScale, stroke, fill, chartHeight, isDisabled }: BpsSeriesProps) => {
  const points: [number, number][] = bpsRecords
    .map(record => [record.year,record.bps]);

  const line = d3
    .line()
    .x(d => xScale(d[0]))
    .y(d => yScale(d[1]));

  const sortedPoints = points.sort((a, b) => b[0] - a[0]);
  const path = line(sortedPoints);

  return (
    <StyledBpsSeries
      style={{ stroke: stroke, fill: 'none' }}
      d={path || ''}
      className={ classNames({'is-disabled': isDisabled}) }
    />
  )
};

interface LineChartProps {
  bpsRecords: BpsRecord[],
  chartWidth: number,
  chartHeight: number,
  selectedSeries: string | undefined,
  yDomain?: any
};

const groupBpsSeries = (bpsRecords: BpsRecord[]): Map<string, BpsRecord[]> => {
  return bpsRecords.reduce((rv, x) => {
    let currentRecords = rv.get(x.region) || [];

    rv.set(x.region, currentRecords.concat([x]));

    return rv;
  }, new Map<string, BpsRecord[]>());
};

const LineChart = ({ bpsRecords, chartWidth, chartHeight, selectedSeries, yDomain }: LineChartProps) => {
  const xScale = d3.scaleLinear()
    // @ts-ignore
    .domain(d3.extent(bpsRecords.map(record => record.year)))
    .range([MARGIN.left, MARGIN.left + chartWidth]);

  const _yDomain = yDomain
    ? yDomain
    // @ts-ignore
    : d3.scaleLinear().domain(d3.extent(bpsRecords.map(record => record.bps)));

  const yScale = _yDomain
    .range([MARGIN.top + chartHeight, MARGIN.top]);

  const groups = Array
    .from(groupBpsSeries(bpsRecords).entries())
    .sort((g1, g2) => Math.max(...g2[1].map(r => r.bps)) - Math.max(...g1[1].map(r => r.bps)));

  return (
    <svg
      viewBox={`0 0 ${MARGIN.left + chartWidth + MARGIN.right} ${MARGIN.top + chartHeight + MARGIN.bottom}`}
    >
      { groups.map(([ region, records ]) => (
          <BpsSeries
            key={ region }
            xScale={ xScale }
            yScale={ yScale }
            bpsRecords={ records }
            chartHeight={ chartHeight }
            stroke={ SERIES_COLORS[region]['stroke'] }
            fill={ SERIES_COLORS[region]['fill'] }
            isDisabled={ selectedSeries !== undefined && selectedSeries !== region }
          />
        ))
      }
      <BorderBox chartWidth={ chartWidth } chartHeight={ chartHeight } />
      <VerticalAxis yScale={ yScale }/>
      <HorizontalAxis xScale={ xScale } chartHeight={ chartHeight } />
    </svg>
  )
};

export default LineChart;
